import { toIntlLocale } from './locale';

/**
 * Даты приходят из API в ISO 8601 (UTC), даты без времени — YYYY-MM-DD (ТЗ API-01).
 * Вывод — через Intl в часовом поясе пользователя (или явно переданном поясе события).
 */
export interface DateFormatOptions {
  /** IANA-пояс; по умолчанию — пояс браузера пользователя. */
  timeZone?: string;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function toDate(value: string | Date): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // Дата без времени — календарный день, не момент: читаем как полдень UTC, чтобы пояс не сдвигал день.
  const date = new Date(DATE_ONLY.test(value) ? `${value}T12:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatDate(
  value: string | Date,
  language: string,
  options: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  const isDateOnly = typeof value === 'string' && DATE_ONLY.test(value);
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: isDateOnly ? 'UTC' : options.timeZone,
  }).format(date);
}

export function formatDateTime(
  value: string | Date,
  language: string,
  options: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date);
}

/**
 * Дата без времени цифрами (ТЗ API-01: `YYYY-MM-DD`): «12.03.1994» (ru), «12/03/1994» (en-GB, es).
 * Календарный день не сдвигается поясом. Некорректная строка возвращается как есть.
 */
export function formatNumericDate(isoDate: string, language: string): string {
  if (!DATE_ONLY.test(isoDate)) return isoDate;
  const date = toDate(isoDate);
  if (!date) return isoDate;
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

export function formatTime(
  value: string | Date,
  language: string,
  options: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: options.timeZone,
  }).format(date);
}

const MILLISECONDS_TIME: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hourCycle: 'h23',
};

/**
 * Момент с миллисекундами — интеграционный журнал (ТЗ 14.10.2: «дата, время с миллисекундами в
 * часовом поясе администратора»): «22.10.2026, 01:14:15,067» (ru), «22/10/2026, 01:14:15.067» (en).
 */
export function formatDateTimeMs(
  value: string | Date,
  language: string,
  options: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...MILLISECONDS_TIME,
    timeZone: options.timeZone,
  }).format(date);
}

/** Только время с миллисекундами: «01:14:15,067». */
export function formatTimeMs(
  value: string | Date,
  language: string,
  options: DateFormatOptions = {},
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(toIntlLocale(language), {
    ...MILLISECONDS_TIME,
    timeZone: options.timeZone,
  }).format(date);
}

/** Один ли календарный день у двух моментов в поясе пользователя (или явно переданном). */
export function isSameCalendarDay(
  first: string | Date,
  second: string | Date,
  options: DateFormatOptions = {},
): boolean {
  const a = toDate(first);
  const b = toDate(second);
  if (!a || !b) return false;
  const day = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: options.timeZone,
  });
  return day.format(a) === day.format(b);
}
